import { useCallback, useEffect, useRef, useState } from "react";

// サイドメニューの並び替え（ドラッグ）。
//   PC：押したまま動かすとドラッグ開始。
//   スマホ：長押し（約0.45秒、指を動かさない）でドラッグ開始。先に指が動いたら普通のスクロール。
// 項目には data-nav-key / data-nav-group / data-nav-next、グループ見出しには data-nav-group-head / data-nav-first、
// スクロールする枠には data-nav-scroll を付ける。

export interface DropTarget { group: string; before: string | null } // before=null はグループの最後

const LONG_PRESS_MS = 450;
const MOUSE_SLOP = 6;
const TOUCH_SLOP = 8;

interface Start { key: string; x: number; y: number; touch: boolean; timer: number | null }

function findTarget(x: number, y: number): DropTarget | null {
  const el = document.elementFromPoint(x, y) as HTMLElement | null;
  const item = el?.closest<HTMLElement>("[data-nav-key]");
  if (item?.dataset.navKey && item.dataset.navGroup) {
    const r = item.getBoundingClientRect();
    const before = y < r.top + r.height / 2 ? item.dataset.navKey : (item.dataset.navNext || null);
    return { group: item.dataset.navGroup, before };
  }
  const head = el?.closest<HTMLElement>("[data-nav-group-head]");
  if (head?.dataset.navGroupHead) return { group: head.dataset.navGroupHead, before: head.dataset.navFirst || null };
  return null;
}

// ドラッグ中に枠の上端・下端へ近づいたら自動でスクロール（遠いグループへ移すため）。止めて押さえている間も続ける。
function autoScroll(x: number, y: number): boolean {
  const box = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>("[data-nav-scroll]");
  if (!box) return false;
  const r = box.getBoundingClientRect();
  const before = box.scrollTop;
  if (y < r.top + 48) box.scrollBy(0, -10);
  else if (y > r.bottom - 48) box.scrollBy(0, 10);
  return box.scrollTop !== before;
}

export function useNavDrag(onDrop: (key: string, t: DropTarget) => void, enabled: boolean) {
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const start = useRef<Start | null>(null);
  const active = useRef<string | null>(null);
  const targetRef = useRef<DropTarget | null>(null);
  const suppressClick = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const raf = useRef(0);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;

  const finish = useCallback((drop: boolean) => {
    const s = start.current;
    if (!s) return;
    if (s.timer) clearTimeout(s.timer);
    const key = active.current;
    const t = targetRef.current;
    start.current = null; active.current = null; targetRef.current = null;
    setDragKey(null); setTarget(null); setPos(null);
    if (key) {
      // ドラッグ（長押し）のあとに続く「クリック」で画面が移動しないようにする
      suppressClick.current = true;
      setTimeout(() => { suppressClick.current = false; }, 400);
      if (drop && t && t.before !== key) dropRef.current(key, t);
    }
  }, []);

  useEffect(() => {
    const begin = (s: Start) => {
      active.current = s.key;
      setDragKey(s.key);
      setPos({ x: s.x, y: s.y });
      try { navigator.vibrate?.(15); } catch { /* 非対応端末 */ }
    };
    const move = (e: PointerEvent) => {
      const s = start.current;
      if (!s) return;
      if (!active.current) {
        const d = Math.hypot(e.clientX - s.x, e.clientY - s.y);
        if (s.touch) { if (d > TOUCH_SLOP) finish(false); return; } // 長押し前に動いた＝スクロール
        if (d <= MOUSE_SLOP) return;
        begin(s);
      }
      last.current = { x: e.clientX, y: e.clientY };
      const t = findTarget(e.clientX, e.clientY);
      targetRef.current = t;
      setTarget(t);
      setPos({ x: e.clientX, y: e.clientY });
      if (!raf.current) {
        const tick = () => {
          raf.current = 0;
          const l = last.current;
          if (!active.current || !l) return;
          if (autoScroll(l.x, l.y)) {
            const t2 = findTarget(l.x, l.y); targetRef.current = t2; setTarget(t2);
            raf.current = requestAnimationFrame(tick);
          }
        };
        raf.current = requestAnimationFrame(tick);
      }
    };
    const up = () => finish(true);
    const cancel = () => finish(false);
    const touchMove = (e: TouchEvent) => { if (active.current) e.preventDefault(); }; // ドラッグ中は画面をスクロールさせない
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") finish(false); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("touchmove", touchMove, { passive: false });
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("touchmove", touchMove);
      window.removeEventListener("keydown", key);
    };
  }, [finish]);

  const onPointerDown = (e: React.PointerEvent, key: string) => {
    if (!enabled || e.button !== 0) return;
    const touch = e.pointerType !== "mouse";
    const s: Start = { key, x: e.clientX, y: e.clientY, touch, timer: null };
    if (touch) s.timer = window.setTimeout(() => { if (start.current === s) { active.current = s.key; setDragKey(s.key); setPos({ x: s.x, y: s.y }); try { navigator.vibrate?.(15); } catch { /* 非対応 */ } } }, LONG_PRESS_MS);
    start.current = s;
  };

  // 項目の枠に付ける：クリック抑止・長押しメニュー抑止
  const containerProps = {
    onClickCapture: (e: React.MouseEvent) => { if (suppressClick.current) { e.preventDefault(); e.stopPropagation(); } },
    onContextMenu: (e: React.MouseEvent) => { if (start.current || active.current) e.preventDefault(); },
  };

  return { dragKey, target, pos, onPointerDown, containerProps };
}
